<script setup lang="ts">
import BaseBottomSheetFilter from '@/components/base/BaseBottomSheetFilter.vue'
import BaseFilter from '@/components/base/BaseFilter.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { FilterMap, FilterOption, FilterSection } from '@/types/filter.types'
import type { OpenClassFilter } from '@/types/open-class.types'
import { reactive, ref, watch } from 'vue'

const isMobile = useIsMobile()

const props = defineProps<{
  initialCategory?: FilterOption
  initialFilters?: OpenClassFilter
}>()

// 카테고리 탭 옵션
const categoryOptions: FilterOption[] = [
  { idx: 0, name: '무료 클래스', data: 'free-class' },
  { idx: 1, name: '게시글', data: 'post' },
]

const selectedCategory = ref<FilterOption>(props.initialCategory || categoryOptions[0])

// 필터 옵션 정의
const levelOptions: FilterOption[] = [
  { idx: 0, name: '전체 레벨', data: 'all' },
  { idx: 1, name: '초급', data: 'beginner' },
  { idx: 2, name: '중급', data: 'intermediate' },
  { idx: 3, name: '고급', data: 'advanced' },
]

const sortOptions: FilterOption[] = [
  { idx: 0, name: '최신순', data: 'latest' },
  { idx: 1, name: '오래된순', data: 'oldest' },
]

// 필터 맵 정의
const filterMap = reactive<FilterMap>({
  level: {
    title: '레벨',
    options: levelOptions,
    selected: props.initialFilters?.level || levelOptions[0],
  },
  sort: {
    title: '정렬',
    options: sortOptions,
    selected: props.initialFilters?.sort || sortOptions[0],
  },
})

// props가 변경되면 상태 업데이트
watch(
  () => props.initialCategory,
  (newVal) => {
    if (newVal) {
      selectedCategory.value = newVal
    }
  },
  { immediate: true },
)

watch(
  () => props.initialFilters,
  (newVal) => {
    if (newVal) {
      filterMap.level.selected = newVal.level
      filterMap.sort.selected = newVal.sort
    }
  },
  { immediate: true },
)

// 바텀시트용 필터 섹션 생성
const getFilterSections = (): FilterSection[] => {
  const sections: FilterSection[] = []

  if (selectedCategory.value.data === 'free-class') {
    sections.push({
      ref: 'level',
      title: '레벨',
      options: levelOptions,
      selectedOption: filterMap.level.selected,
    })
  }

  sections.push({
    ref: 'sort',
    title: '정렬',
    options: sortOptions,
    selectedOption: filterMap.sort.selected,
  })

  return sections
}

// 이벤트 정의
const emit = defineEmits<{
  (e: 'change', category: FilterOption, filters: FilterMap): void
}>()

// 카테고리 변경 핸들러
const handleCategoryChange = (category: FilterOption) => {
  selectedCategory.value = category
  emitChange()
}

// 옵션 선택 핸들러
const handleLevelSelect = (option: FilterOption) => {
  filterMap.level.selected = option
  emitChange()
}

const handleSortSelect = (option: FilterOption) => {
  filterMap.sort.selected = option
  emitChange()
}

// 바텀시트에서 저장 시 호출되는 핸들러
const handleSave = (selectedOptions: FilterOption[]) => {
  const sections = getFilterSections()

  // 선택된 옵션 적용
  sections.forEach((section, index) => {
    if (selectedOptions[index] && section.ref in filterMap) {
      const key = section.ref as keyof typeof filterMap
      filterMap[key].selected = selectedOptions[index]
    }
  })

  emitChange()
}

// 변경사항을 부모 컴포넌트에 전달
const emitChange = () => {
  emit('change', selectedCategory.value, filterMap)
}
</script>

<template>
  <div class="open-class-filter">
    <!-- 카테고리 선택 버튼 -->
    <div class="category-tabs">
      <div
        v-for="category in categoryOptions"
        :key="category.data"
        class="category-tab title-md"
        :class="{ active: selectedCategory.data === category.data, 'title-sm': isMobile }"
        @click="handleCategoryChange(category)"
      >
        {{ category.name }}
      </div>
    </div>

    <!-- 필터 옵션들 (PC 버전) -->
    <div v-if="!isMobile" class="filter-options">
      <BaseFilter
        v-if="selectedCategory.data === 'free-class'"
        :options="filterMap.level.options"
        :selectedOption="filterMap.level.selected"
        @select="handleLevelSelect"
      />
      <BaseFilter
        :options="filterMap.sort.options"
        :selectedOption="filterMap.sort.selected"
        @select="handleSortSelect"
      />
    </div>

    <!-- 필터 옵션들 (모바일 버전) -->
    <div v-else class="filter-options-mobile">
      <BaseBottomSheetFilter :filterSections="getFilterSections()" @save="handleSave" />
    </div>
  </div>
</template>

<style scoped>
.open-class-filter {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
}

.category-tabs {
  display: flex;
  gap: 20px;
}

.category-tab {
  padding: 0px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.2s ease;
}

.category-tab.active {
  color: var(--white);
}

.category-tab:not(.active) {
  color: var(--gray-600);
}

.filter-options {
  display: flex;
  gap: 12px;
  align-items: center;
}

@media (max-width: 1365px) {
  .open-class-filter {
    padding: 16px 20px;
    margin-bottom: 0px;
  }

  .category-tabs {
    height: auto;
    gap: 12px;
    align-items: center;
  }

  .category-tab {
    height: auto;
    padding: 0px;
  }
}
</style>
